import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { toast } from "sonner";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  Chip,
  Box,
  Divider,
} from "@material-ui/core";
import { CheckCircle, Description } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  templateList: {
    width: "100%",
    maxHeight: 400,
    overflow: "auto",
  },
  templateItem: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedTemplate: {
    backgroundColor: theme.palette.action.selected,
  },
  templateHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  approvedChip: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  pendingChip: {
    backgroundColor: "#ff9800",
    color: "#fff",
  },
  rejectedChip: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
  templateContent: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    fontSize: "0.875rem",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
    color: theme.palette.text.secondary,
  },
}));

const TemplateSelectorModal = ({ modalOpen, onClose, onSelect, whatsappId, ticketId, onTemplateSent }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingInUse, setMarkingInUse] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (!modalOpen || !whatsappId) {
      return;
    }
    // Resetar seleção ao abrir modal para garantir que busque do cache
    setSelectedTemplate(null);
    fetchTemplates();
  }, [modalOpen, whatsappId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates/list", {
        params: { whatsappId },
      });

      // Filtrar apenas templates aprovados
      const approvedTemplates = data.templates.filter(
        (template) => template.status === "APPROVED"
      );

      setTemplates(approvedTemplates);

      // Após carregar templates, buscar template em uso e pré-selecionar
      if (approvedTemplates.length > 0) {
        await fetchTemplateInUse(approvedTemplates);
      }
    } catch (err) {
      toastError(err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateInUse = async (templatesList) => {
    try {
      const { data } = await api.get("/templates/in-use");
      
      if (data.templateId && templatesList && templatesList.length > 0) {
        // Buscar o template correspondente na lista e pré-selecionar
        const template = templatesList.find(t => t.id === data.templateId);
        if (template) {
          setSelectedTemplate(template);
        }
      }
    } catch (err) {
      // Não bloquear o fluxo se falhar
      console.error("Erro ao buscar template em uso:", err);
    }
  };

  const handleClose = () => {
    // Não resetar selectedTemplate - será carregado do cache quando abrir novamente
    onClose();
  };

  const handleMarkTemplateInUse = async (templateId) => {
    try {
      await api.post("/templates/mark-in-use", {
        templateId
      });
    } catch (err) {
      // Não bloquear o fluxo se falhar - apenas logar erro
      console.error("Erro ao marcar template em uso:", err);
    }
  };

  const handleSelectTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      setMarkingInUse(true);
      // Marcar template em uso
      await handleMarkTemplateInUse(selectedTemplate.id);
      
      // Se tiver ticketId, significa que está no contexto de iniciar conversa
      // Deve enviar o template automaticamente
      if (ticketId) {
        try {
          await api.post("/templates/send", {
            ticketId,
            templateId: selectedTemplate.id
          });
          
          toast.success("Template enviado com sucesso!");
          
          // Se tiver callback onTemplateSent, chamar
          if (onTemplateSent) {
            onTemplateSent();
          }
        } catch (err) {
          toastError(err);
          // Continuar mesmo se falhar o envio
        }
      }
      
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setMarkingInUse(false);
    }
  };

  const getTemplateBody = (template) => {
    if (!template.components || template.components.length === 0) {
      return "Sem preview disponível";
    }

    const bodyComponent = template.components.find(
      (comp) => comp.type === "BODY"
    );

    if (bodyComponent && bodyComponent.text) {
      return bodyComponent.text;
    }

    return "Sem preview disponível";
  };

  const getStatusChipClass = (status) => {
    switch (status) {
      case "APPROVED":
        return classes.approvedChip;
      case "PENDING":
        return classes.pendingChip;
      case "REJECTED":
        return classes.rejectedChip;
      default:
        return "";
    }
  };

  const renderTemplateList = () => {
    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      );
    }

    if (templates.length === 0) {
      return (
        <div className={classes.emptyContainer}>
          <Description style={{ fontSize: 48, marginBottom: 16 }} />
          <Typography variant="body1">
            Nenhum template disponível
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Certifique-se de ter templates aprovados no Notificame
          </Typography>
        </div>
      );
    }

    return (
      <List className={classes.templateList}>
        {templates.map((template, index) => (
          <React.Fragment key={template.id}>
            <ListItem
              button
              className={`${classes.templateItem} ${
                selectedTemplate?.id === template.id
                  ? classes.selectedTemplate
                  : ""
              }`}
              onClick={() => setSelectedTemplate(template)}
            >
              <ListItemText
                primary={
                  <Box className={classes.templateHeader}>
                    <Typography variant="subtitle1" component="span">
                      {template.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={template.language}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={template.status}
                      className={`${classes.statusChip} ${getStatusChipClass(
                        template.status
                      )}`}
                    />
                    {template.isInUse && (
                      <Chip
                        size="small"
                        label="Em Uso"
                        style={{ 
                          backgroundColor: "#4caf50", 
                          color: "#fff",
                          marginLeft: 8
                        }}
                      />
                    )}
                    {selectedTemplate?.id === template.id && (
                      <CheckCircle
                        style={{ color: "#4caf50", marginLeft: "auto" }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      Categoria: {template.category}
                    </Typography>
                    <Box className={classes.templateContent}>
                      <Typography variant="body2">
                        {getTemplateBody(template)}
                      </Typography>
                    </Box>
                  </>
                }
              />
            </ListItem>
            {index < templates.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {i18n.t("templateSelector.title")}
      </DialogTitle>
      <DialogContent dividers>{renderTemplateList()}</DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          disabled={loading}
          variant="outlined"
        >
          {i18n.t("templateSelector.buttons.cancel")}
        </Button>
        <ButtonWithSpinner
          variant="contained"
          type="button"
          disabled={!selectedTemplate || loading || markingInUse}
          onClick={handleSelectTemplate}
          color="primary"
          loading={loading || markingInUse}
        >
          {i18n.t("templateSelector.buttons.select")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSelectorModal;

